import React, { useState, useEffect } from 'react';
import { Spin } from 'antd';
import {
  Table,
  TableContainer,
  sortDirections,
} from '../../../../components/customTableComponents';
import CallListHead from './components/CallListHead/CallListHead';
import CallListBody from './components/CallListBody/CallListBody';
import PageSizeButton from '../../../../components/PageSizeButton/PageSizeButton';
import DspxPagination from '../../../../components/DspxPagination/DspxPagination';
import CallDirection from '../CallDirection/CallDirection';
import { stableSort } from '../../../../helpers/sortFns';
import usePagination from '../../../../hooks/usePagination';
import { filterKeys } from './constants';
import { getValueByPath } from '../../../../helpers/getValueByPath';
import styles from './CallList.module.scss';

let initRowsPerPage = 10;

const FilterItemContainer = ({ children }) => {
  return <div className={styles.filterItemContainer}>{children}</div>;
};

const filters = {
  [filterKeys.direction]: [
    {
      value: 'in',
      text: (
        <FilterItemContainer>
          <CallDirection direction="in" />
          <span>Входящие</span>
        </FilterItemContainer>
      ),
    },
    {
      value: 'out',
      text: (
        <FilterItemContainer>
          <CallDirection direction="out" />
          <span>Исходящие</span>
        </FilterItemContainer>
      ),
    },
  ],
};

const CallList = ({ calls, isCallsLoading }) => {
  const [order, setOrder] = useState('');
  const [orderBy, setOrderBy] = useState('');
  const [sortType, setSortType] = useState('');
  const [rowsPerPage, setRowsPerPage] = useState(initRowsPerPage);
  const [filteredInfo, setFilteredInfo] = useState(null);
  const [filteredCalls, setFilteredCalls] = useState([]);
  const [sortedCalls, setSortedCalls] = useState([]);

  // Фильтруем звонки
  useEffect(() => {
    let newFilteredCalls = calls || [];

    if (filteredInfo && newFilteredCalls.length) {
      newFilteredCalls = Object.entries(filteredInfo).reduce(
        (acc, [key, value]) => {
          if (!value.length) {
            return acc;
          }

          if (key === filterKeys.direction) {
            return acc.filter((item) => {
              const direction = value[0];
              if (direction === 'in') {
                return item.direction === direction;
              }

              if (direction === 'out') {
                return (
                  item.direction === direction || item.direction === 'callback'
                );
              }
            });
          }

          return acc.filter((item) => item.includes(getValueByPath(key)));
        },
        newFilteredCalls
      );
    }

    setFilteredCalls(newFilteredCalls);
  }, [calls, filteredInfo]);

  // Сортируем отфильтрованные звонки
  useEffect(() => {
    const newSortedCalls = stableSort({
      arr: filteredCalls,
      order,
      orderBy,
      sortType,
    });
    setSortedCalls(newSortedCalls);
  }, [filteredCalls, order, orderBy, sortType]);

  const [currentPageNumber, setCurrentPageNumber, maxPage, shownItems] =
    usePagination(sortedCalls, rowsPerPage);

  const handleRecordsSort = ({ property, sortType }) => {
    if (orderBy === property) {
      const newOrder =
        order === ''
          ? sortDirections.asc
          : order === sortDirections.asc
          ? sortDirections.desc
          : '';

      setOrder(newOrder);
    } else {
      setOrderBy(property);
      setSortType(sortType);
      setOrder(sortDirections.asc);
    }
  };

  const handleChangeRowsPerPage = (newRowsPerPage) => {
    setRowsPerPage(newRowsPerPage);
    initRowsPerPage = newRowsPerPage;
  };

  const handleChangePage = (page) => {
    const pageNum = page - 1;
    setCurrentPageNumber(pageNum);
  };

  return (
    <Spin spinning={isCallsLoading}>
      <TableContainer className={styles.root}>
        <Table>
          <CallListHead
            onRecordsSort={handleRecordsSort}
            filteredInfo={filteredInfo}
            setFilteredInfo={setFilteredInfo}
            orderBy={orderBy}
            order={order}
            filters={filters}
          />
          <CallListBody calls={shownItems} />
        </Table>
        <div className={styles.paginationContainer}>
          <PageSizeButton
            pageSize={rowsPerPage}
            setPageSize={handleChangeRowsPerPage}
            className={styles.pageSizeBtnWrap}
            buttonClassName={styles.pageSizeBtn}
          />
          <DspxPagination
            current={currentPageNumber + 1}
            total={calls?.length || 0}
            pageSize={rowsPerPage}
            showQuickJumper={maxPage > 9}
            quickJumperToStart
            onChange={handleChangePage}
            hideOnSinglePage
          />
        </div>
      </TableContainer>
    </Spin>
  );
};

export default CallList;
